import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/common/Button";
import { useSettingsStore } from "@/stores/settingsStore";
import {
  SERVICE_KEYS,
  useOnboardingStore,
} from "@/stores/onboardingStore";
import { AnimatedCheckmark } from "../primitives/AnimatedCheckmark";

/**
 * Final step — persists whatever the user actually typed (usually nothing:
 * credentials are provisioned, not collected) plus ``setup_complete``, in a
 * single update call, then hands control back to the root App which swaps in
 * the main shell.
 *
 * We save here (not in earlier steps) so the "you're ready" screen acts
 * as the single commit point: if the save fails, the user sees a clear
 * error without having been pushed into the main app prematurely.
 */
export function AllSetStep() {
  const { token, tokenValidation, services, servicesValidation } =
    useOnboardingStore();
  const { updateSettings, loadSettings, canEditGlobal } = useSettingsStore();
  // Which rows already hold a value — provisioned (ADR-0014) or set by an
  // admin earlier. Onboarding adds to these; it must never overwrite them.
  const settings = useSettingsStore((s) => s.settings);
  const isSet = (key: string) => settings[key]?.set === true;

  const [saving, setSaving] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedOnce, setSavedOnce] = useState(false);

  /** Build the full payload of secrets + sane defaults to write to settings. */
  const buildPayload = useCallback((): Record<string, string> => {
    const payload: Record<string, string> = { setup_complete: "true" };
    // ``token`` holds an OpenRouter key only when the user typed one into the
    // provisioning step's bring-your-own fallback. Write it ONLY then: an
    // empty write would store a blank secret AND flip provenance to byok,
    // unprovisioning the user for good.
    const openrouter = token.trim();
    if (openrouter) payload.openrouter_api_key = openrouter;
    // Shared workspace config is admin-managed on cloud: a non-admin's
    // onboarding must not (and now cannot — the backend 403s) write global
    // keys. Pre-gate, every new cloud user's onboarding silently CLOBBERED
    // the org's opensearch config with these defaults.
    if (canEditGlobal) {
      // Defaults the old SetupWizard wrote alongside the secrets — needed by
      // the agent-side code, but only where provisioning left a hole: writing
      // them unconditionally would stamp the hardcoded host over the one the
      // org just handed us (a cluster move would be undone on every install).
      if (!isSet("opensearch_host")) payload.opensearch_host = "search.api.nbyula.com";
      if (!isSet("opensearch_port")) payload.opensearch_port = "443";
      if (!isSet("pplx_model")) payload.pplx_model = "sonar-deep-research";
      if (!isSet("max_agent_turns")) payload.max_agent_turns = "50";
      for (const key of SERVICE_KEYS) {
        const value = services[key].trim();
        if (value) payload[key] = value;
      }
    }
    return payload;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, services, canEditGlobal, settings]);

  useEffect(() => {
    if (savedOnce) return;
    // Nothing is gated on the user having *typed* a credential any more — the
    // org provisions them (ADR-0014), so most people arrive here with every
    // field already filled and every validation still "idle". The only thing
    // worth refusing is a value the user typed that failed to verify: writing
    // a typo would break the install we just set up.
    const badlyTyped = [
      ...(token.trim() && tokenValidation.kind !== "verified"
        ? ["Your OpenRouter key"] : []),
      ...(canEditGlobal
        ? SERVICE_KEYS.filter(
            (k) => services[k].trim() && servicesValidation[k].kind !== "verified",
          )
        : []),
    ];
    if (badlyTyped.length > 0) {
      setSaveError(
        "Some credentials you entered weren't verified. Please go back and fix them.",
      );
      setSaving(false);
      return;
    }
    setSavedOnce(true);
    setSaving(true);
    setSaveError(null);

    updateSettings(buildPayload())
      .then(() => loadSettings())
      .then(() => setSaving(false))
      .catch((e) => {
        setSaveError(
          (e as Error).message ?? "Couldn't save your setup. Try again.",
        );
        setSaving(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await updateSettings(buildPayload());
      await loadSettings();
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (saving) {
    return (
      <div className="flex flex-col items-center text-center">
        <div className="mb-5 h-12 w-12 animate-spin rounded-full border-2 border-jove-50 border-t-jove" />
        <p className="text-[14px] text-space-50">Saving your setup…</p>
      </div>
    );
  }

  if (saveError) {
    return (
      <div className="text-center">
        <h1 className="text-[32px] font-bold leading-[1.1] tracking-[-0.01em] text-space">
          Almost there
        </h1>
        <p className="mt-2 text-[14px] text-hydra">{saveError}</p>
        <Button variant="primary" size="md" className="mt-5" onClick={handleRetry}>
          Try saving again
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-6 onb-scale-spring">
        <AnimatedCheckmark size={56} tone="oxy" ringed delay={60} />
      </div>
      <h1 className="text-[32px] font-bold leading-[1.1] tracking-[-0.01em] text-space">
        You're ready
      </h1>
      <p className="mt-2 max-w-sm text-[14px] leading-relaxed text-space-50">
        OmniWerk is set up and connected.
        Let's find some candidates.
      </p>

      <Button
        variant="primary"
        size="lg"
        className="mt-7 min-w-[200px]"
        onClick={() => {
          // No explicit nav — loadSettings flipped setup_complete, so the
          // App root will swap in the main shell on its next render.
          loadSettings();
        }}
      >
        Open OmniWerk
      </Button>
    </div>
  );
}
